import React from "react";
import styles from "./BulkSharing.module.scss";
import helperStyles from "@src/components/helpers.module.scss";
import { EventInventoryRecord } from "@src/store/pricing/queryEventInventoryAllSlice";
import { clx } from "../utils/stringUtils";
import { useAppSelector } from "@src/store/hooks";
import { formatCurrency } from "../utils/numberUtils";
import { X } from "lucide-react";
import { Button, ButtonVariant } from "../button/Button";
import { Loader } from "../loader/Loader";
import { TExportDestinations } from "@src/store/system/listExportDestinationsSlice";
import { buildMarketplaceUnits, getSplitsName } from "../utils/marketplaceUtils";
import { MarketplaceIcon } from "../marketPlaces/MarketplaceIcon";
import DisplayMarketplace from "../displayMarketplace/DisplayMarketplace";
import displayStyles from "../displayMarketplace/DisplayMarketplace.module.scss";
import { MyTooltip } from "../tooltip/Tooltip";
import { ExportDestination } from "@src/constants/constants";

type TBulkSharingItem = {
	data: EventInventoryRecord;
	loading?: boolean;
	onRemove?: (x: number) => void;
	sharedDestinations?: Array<TExportDestinations>;
	disableTooltip?: boolean;
};

const BulkSharingItem = ({ data, loading, sharedDestinations, disableTooltip, onRemove }: TBulkSharingItem) => {
	const event = useAppSelector((state) => state.pricing.selection.event);
	const hasCIMSMarketAccess = useAppSelector((state) => state.settings.hasCIMSMarketAccess);
	const allDestinations = useAppSelector((state) => state.system.exportDestinations.data);

	const mappedMarketplaces = buildMarketplaceUnits(
		sharedDestinations?.filter((x) => hasCIMSMarketAccess || x.Export_Destination_ID !== ExportDestination.Cims) ?? [],
		allDestinations,
	).map((unit) => {
		if (unit.destinations.length === 1) {
			return <DisplayMarketplace key={unit.key} data={unit.destinations[0]} disableTooltip={disableTooltip} />;
		}

		const chip = (
			<Button style={{ padding: 6, display: "flex", gap: 6 }} variant={ButtonVariant.Secondary}>
				{unit.destinations.map((d) => (
					<div key={d.Export_Destination_ID} className={displayStyles.imageContainer}>
						<MarketplaceIcon destination={d} />
					</div>
				))}
			</Button>
		);

		return disableTooltip ? (
			<React.Fragment key={unit.key}>{chip}</React.Fragment>
		) : (
			<MyTooltip
				key={unit.key}
				id={`bulk-shared-${data.Item_ID}-${unit.key}`}
				popup={unit.label}
				elementStyle={{ padding: 6 }}
				element={chip}
			/>
		);
	});

	const splitName = getSplitsName(data.StubHub_Splits);

	return (
		<div key={data.Item_ID} className={styles.singleListing}>
			<div className={styles.secRowSeat}>
				<div className={styles.secRowSeatCont}>
					<div style={{ userSelect: "none" }} className={clx(styles.itemTextPrimary, helperStyles.ellipsisDiv)}>
						{event?.Event_Headliner}
					</div>
					<div className={styles.itemTextPrimary}>
						{data.Section}, Row {data.Row}
					</div>
					<div style={{ gap: "2px 8px" }} className={clx(styles.textSecondary, helperStyles.flexSmallGap)}>
						<div>ListingID: {data.Listing_ID}</div>
						<div>Seats: {data.Seats}</div>
					</div>
					<div className={clx(styles.textSecondary, helperStyles.flexSmallGap)}>
						<div>Shared to:</div>
						{loading ? (
							<Button style={{ padding: 9 }} variant={ButtonVariant.Secondary}>
								<Loader />
							</Button>
						) : mappedMarketplaces?.length ? (
							mappedMarketplaces
						) : (
							<X size={16} />
						)}
					</div>
				</div>
			</div>
			<div className={clx(styles.qty, helperStyles.ellipsisDiv)}>{data.Quantity}</div>
			<div className={clx(styles.splitLabel, helperStyles.ellipsisDiv)}>{splitName}</div>
			<div className={clx(styles.priceLabel, helperStyles.ellipsisDiv)}>{formatCurrency(data.Item_Price)}</div>
			<div onClick={() => onRemove?.(data.Listing_ID)} className={styles.xContainer}>
				<div>
					<X size={16} />
				</div>
			</div>
		</div>
	);
};

export default BulkSharingItem;
